import React, { useState, useEffect, useMemo } from "react";
import { getData, postData, putData, deleteData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaPuzzlePiece,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle,
  FaFilter
} from "react-icons/fa";

function SkuAccessoryRefsManager() {
  const [references, setReferences] = useState([]);
  const [skus, setSkus] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingRef, setEditingRef] = useState(null);
  const [deletingRef, setDeletingRef] = useState(null);
  const [filterSkuId, setFilterSkuId] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    sku_id: '',
    product_accessory_id: '',
    active: true
  });

  useEffect(() => {
    loadReferences();
    loadSkus();
    loadAccessories();
  }, []);

  useEffect(() => {
    if (filterSkuId) {
      loadReferences(filterSkuId);
    } else {
      loadReferences();
    }
  }, [filterSkuId]);

  const loadReferences = async (skuId = null) => {
    try {
      setLoading(true);
      
      const url = skuId 
        ? `/api/admin/get_sku_accessory_refs?sku_id=${skuId}`
        : '/api/admin/get_sku_accessory_refs';
      
      const response = await getData(url);
      
      if (response && response.data) {
        // La respuesta puede venir de diferentes formas según la API
        let refsData = [];
        if (Array.isArray(response.data)) {
          refsData = response.data;
        } else if (response.data.references && Array.isArray(response.data.references)) {
          refsData = response.data.references;
        } else if (response.data.skuAccessoryRefs && Array.isArray(response.data.skuAccessoryRefs)) {
          refsData = response.data.skuAccessoryRefs;
        } else {
          // Buscar cualquier array en la respuesta
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key]) && refsData.length === 0) {
              refsData = response.data[key];
            }
          });
        }
        
        setReferences(refsData);
      } else {
        setReferences([]);
      }
      
    } catch (error) {
      console.error('Error cargando referencias:', error);
      toastError('Error al cargar las referencias SKU-Accesorio');
      setReferences([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSkus = async () => {
    try {
      const response = await getData('/api/admin/get_sku_mains');
      
      let skusData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          skusData = response.data;
        } else if (response.data.skus && Array.isArray(response.data.skus)) {
          skusData = response.data.skus;
        } else if (response.data.skuMains && Array.isArray(response.data.skuMains)) {
          skusData = response.data.skuMains;
        } else {
          // Buscar cualquier array en la respuesta
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key]) && skusData.length === 0) {
              skusData = response.data[key];
            }
          });
        }
      }
      
      // Filtrar solo SKUs activos
      const activeSkus = skusData.filter(sku => sku.active);
      setSkus(activeSkus);
      
    } catch (error) {
      console.error('Error cargando SKUs:', error);
      toastError('Error al cargar los SKUs');
      setSkus([]);
    }
  };

  const loadAccessories = async () => {
    try {
      const response = await getData('/api/admin/get_accessories');
      
      let accessoriesData = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          accessoriesData = response.data;
        } else if (response.data.accessories && Array.isArray(response.data.accessories)) {
          accessoriesData = response.data.accessories;
        } else {
          // Buscar cualquier array en la respuesta
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key]) && accessoriesData.length === 0) {
              accessoriesData = response.data[key];
            }
          });
        }
      }
      
      // Filtrar solo accesorios activos
      const activeAccessories = accessoriesData.filter(accessory => accessory.active);
      setAccessories(activeAccessories);
      
    } catch (error) {
      console.error('Error cargando accesorios:', error);
      toastError('Error al cargar los accesorios');
      setAccessories([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          sku_id: parseInt(formData.sku_id),
          product_accessory_id: parseInt(formData.product_accessory_id),
          active: formData.active ? 1 : 0,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      // Validar si ya existe esta combinación (solo para crear, no para editar)
      if (!editingRef) {
        const existingRef = references.find(
          ref => ref.sku_id === dataToSend.data.sku_id && 
                 ref.product_accessory_id === dataToSend.data.product_accessory_id
        );
        
        if (existingRef) {
          toastError('Esta combinación de SKU y accesorio ya existe');
          setLoading(false);
          return;
        }
      }

      if (editingRef) {
        const response = await putData('/api/admin/update_sku_accessory_ref', dataToSend);
        
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al actualizar');
        }
        
        setReferences(prev => prev.map(ref => 
          ref.sku_id === parseInt(formData.sku_id) && ref.product_accessory_id === parseInt(formData.product_accessory_id)
            ? {
                ...ref,
                sku_name: skus.find(s => s.id === parseInt(formData.sku_id))?.sku_description || ref.sku_name,
                accessory_name: accessories.find(a => a.id === parseInt(formData.product_accessory_id))?.accessory_name || ref.accessory_name,
                active: formData.active ? 1 : 0
              }
            : ref
        ));
        
        toastSuccess('Referencia actualizada exitosamente');
      } else {
        const response = await postData('/api/admin/create_sku_accessory_ref', dataToSend);
        
        if (response.response && response.response.status >= 400) {
          throw new Error(response.response.data?.msg || 'Error al crear');
        }
        
        // Actualización optimista
        const newRef = {
          sku_id: parseInt(formData.sku_id),
          product_accessory_id: parseInt(formData.product_accessory_id),
          sku_name: skus.find(s => s.id === parseInt(formData.sku_id))?.sku_description || '',
          sku: skus.find(s => s.id === parseInt(formData.sku_id))?.sku || '',
          accessory_name: accessories.find(a => a.id === parseInt(formData.product_accessory_id))?.accessory_name || '',
          active: formData.active ? 1 : 0
        };
        setReferences(prev => [newRef, ...prev]);
        
        toastSuccess('Referencia creada exitosamente');
        loadReferences(filterSkuId || null).catch(() => {});
      }
      
      setShowModal(false);
      resetForm();
    } catch (error) {
   
      
      // Mostrar el mensaje de error del servidor si está disponible
      let errorMsg = editingRef ? 'Error al actualizar la referencia' : 'Error al crear la referencia';
      if (error.response && error.response.data && error.response.data.msg) {
        errorMsg = error.response.data.msg;
      }
      
      toastError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingRef(null);
    setFormData({
      sku_id: '',
      product_accessory_id: '',
      active: true
    });
  };

  const handleEdit = (ref) => {
    setEditingRef(ref);
    setFormData({
      sku_id: ref.sku_id || '',
      product_accessory_id: ref.product_accessory_id || '',
      active: ref.active !== undefined ? ref.active : true
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingRef(null);
    setFormData({
      sku_id: '',
      product_accessory_id: '',
      active: true
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (ref) => {
    setDeletingRef(ref);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingRef) return;
    
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          sku_id: deletingRef.sku_id,
          product_accessory_id: deletingRef.product_accessory_id
        }
      };

      // Usar deleteData con body
      const response = await deleteData('/api/admin/delete_sku_accessory_ref', dataToSend);
      
      // Verificar si la respuesta es un error
      if (response.response && response.response.status >= 400) {
        throw new Error(response.response.data?.msg || 'Error al eliminar');
      }
      
      toastSuccess('Referencia eliminada exitosamente');
      setShowDeleteModal(false);
      setDeletingRef(null);
      await loadReferences(filterSkuId || null);
    } catch (error) {
      console.error('Error eliminando referencia:', error);
      toastError('Error al eliminar la referencia');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingRef(null);
  };

  const handleFilterChange = (e) => {
    setFilterSkuId(e.target.value);
  };

  const clearFilter = () => {
    setFilterSkuId('');
  };

  const getSkuDisplayName = (skuId) => {
    const sku = skus.find(s => s.id === skuId);
    if (!sku) return `SKU #${skuId}`;
    
    // Priorizar descripción, luego nombre, luego ID
    return sku.sku_description || sku.sku_name || `SKU #${skuId}`;
  };

  const getAccessoryName = (accessoryId) => {
    const accessory = accessories.find(a => a.id === accessoryId);
    return accessory ? accessory.accessory_name : `Accesorio #${accessoryId}`;
  };

  // Filtrar referencias según el toggle - Optimizado con useMemo
  const filteredReferences = useMemo(() => {
    return showInactive ? references : references.filter(ref => ref.active);
  }, [references, showInactive]);

  return (
    <div className="sku-accessory-refs-manager">
      <div className="card">
        <div className="card-header">
          <h5 className="card-title mb-3">
            <FaPuzzlePiece className="me-2 text-primary" />
            Referencias SKU-Accesorio
          </h5>
          
          {/* Filtros */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <FaFilter />
                </span>
                <select
                  className="form-select"
                  value={filterSkuId}
                  onChange={handleFilterChange}
                  disabled={loading}
                >
                  <option value="">
                    {skus.length === 0 ? 'No hay SKUs disponibles' : 'Todos los SKUs'}
                  </option>
                  {skus.map(sku => (
                    <option key={sku.id} value={sku.id}>
                      {sku.sku_description || sku.sku_name || `SKU #${sku.id}`}
                    </option>
                  ))}
                </select>
                {filterSkuId && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={clearFilter}
                    disabled={loading}
                    title="Limpiar filtro"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <div className="col-md-6 text-end">
              <div className="d-flex gap-2 justify-content-end align-items-center">
                <button
                  className="btn btn-primary"
                  onClick={handleNew}
                  disabled={loading}
                >
                  <FaPlus className="me-2" />
                  Nueva Referencia
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card-body">
          {!showInactive && references.length > filteredReferences.length && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                <i className="fas fa-info-circle me-2"></i>
                Hay <strong>{references.length - filteredReferences.length}</strong> elemento(s) inactivo(s) oculto(s).
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowInactive(true)}
              >
                Mostrar todos
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando referencias...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Accesorio</th>
                    <th>Tipo</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReferences.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-muted py-4">
                        {filterSkuId ? (
                          showInactive ? 'No hay referencias para el SKU seleccionado' : 'No hay referencias activas para el SKU seleccionado'
                        ) : (
                          showInactive ? 'No hay referencias registradas' : 'No hay referencias activas. Activa el toggle "Mostrar inactivos" para ver referencias eliminadas.'
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredReferences.map((ref, index) => (
                      <tr key={`${ref.sku_id}-${ref.product_accessory_id}-${index}`}>
                        <td>
                          <span className="badge bg-primary">
                            {getSkuDisplayName(ref.sku_id)}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-success">
                            {getAccessoryName(ref.product_accessory_id)}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {accessories.find(a => a.id === ref.product_accessory_id)?.accessory_type_name || 'N/A'}
                          </small>
                        </td>
                        <td>
                          <span className={`badge ${ref.active ? 'bg-success' : 'bg-danger'}`}>
                            {ref.active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {ref.inserted_date ? new Date(ref.inserted_date).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(ref)}
                            disabled={loading}
                            title="Editar referencia"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(ref)}
                            disabled={loading}
                            title="Eliminar referencia"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal para crear/editar referencia */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingRef ? 'Editar Referencia SKU-Accesorio' : 'Nueva Referencia SKU-Accesorio'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">SKU *</label>
                    <select
                      className="form-select"
                      name="sku_id"
                      value={formData.sku_id}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      <option value="">
                        {skus.length === 0 ? 'No hay SKUs disponibles' : 'Seleccionar SKU'}
                      </option>
                      {skus.map(sku => (
                        <option key={sku.id} value={sku.id}>
                          {sku.sku_description || sku.sku_name || `SKU #${sku.id}`}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      {skus.length === 0 
                        ? 'No se encontraron SKUs activos. Verifica que existan SKUs principales configurados.'
                        : 'Selecciona el SKU principal'
                      }
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Accesorio *</label>
                    <select
                      className="form-select"
                      name="product_accessory_id"
                      value={formData.product_accessory_id}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      <option value="">
                        {accessories.length === 0 ? 'No hay accesorios disponibles' : 'Seleccionar Accesorio'}
                      </option>
                      {accessories.map(accessory => (
                        <option key={accessory.id} value={accessory.id}>
                          {accessory.accessory_name} - {accessory.accessory_type_name}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      {accessories.length === 0 
                        ? 'No se encontraron accesorios activos. Verifica que existan accesorios configurados.'
                        : 'Selecciona el accesorio asociado'
                      }
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="active"
                        checked={formData.active}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                      <label className="form-check-label">
                        Referencia activa
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.sku_id || !formData.product_accessory_id}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingRef ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar la referencia entre:</p>
                <ul>
                  <li><strong>SKU:</strong> {getSkuDisplayName(deletingRef?.sku_id)}</li>
                  <li><strong>Accesorio:</strong> {getAccessoryName(deletingRef?.product_accessory_id)}</li>
                </ul>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  Esta acción eliminará permanentemente la relación y no se puede deshacer.
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SkuAccessoryRefsManager;
